/* ---------------------------------------------------------------------------
(c) 2023-2024, HID Global Corporation, part of ASSA ABLOY.
All rights reserved.

This program is an un published copyrighted work which is proprietary
to HID Global Corporation. This computer program includes Confidential,
Proprietary Information and is a Trade Secret of HID Global Corporation.
Any use, disclosure, modification and/or reproduction is prohibited
unless authorized in writing by HID Global Corporation.

WARNING:  Unauthorized reproduction of this program as well as
unauthorized preparation of derivative works based upon the
program or distribution of copies by sale, rental, lease or
lending are violations of federal copyright laws and state trade
secret laws, punishable by civil and criminal penalties.
--------------------------------------------------------------------------- */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as CryptoJS from 'crypto-js';
import { HttpClient } from '@angular/common/http';

import { SharedService } from '../shared/services/shared.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  logoutRef = false;

  constructor(
    private router: Router,
    private sharedService: SharedService,
    public dialogRef: MatDialog,
    private httpClient: HttpClient
  ) {
    this.isLoggedIn.next(this.isAuthenticated());
  }

  // Generate code verifier
  generateCodeVerifier = (): string =>
    this.sharedService.generateRandomString(43, 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~');

  // Generate code challenge
  generateCodeChallenge = (code_verifier: string): string => {
    return this.base64URL(CryptoJS.SHA256(code_verifier));
  };

  // convert it to base64url
  base64URL = (str: any): string => {
    return str.toString(CryptoJS.enc.Base64).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  };
  // clear the complete session storage
  clearStorage(): void {
    sessionStorage.clear();
  }

  // check for the id token
  hasToken(): boolean {
    if (this.getIdToken()) {
      return true;
    }
    return false;
  }

  // get a id token which is present in session storage
  getIdToken(): string {
    return this.getDecodedInfo('idToken');
  }

  // get a access token which is present in session storage
  getAccessToken(): string {
    return this.getDecodedInfo('accessToken');
  }

  setAuthenticated() {
    sessionStorage.setItem('loggedIn', 'true');
    this.isLoggedIn.next(true);
    this.logoutRef = false;
  }

  isAuthenticated(): boolean {
    return sessionStorage.getItem('loggedIn') === 'true' && sessionStorage.getItem(this.getTenantId()) !== undefined;
  }

  // removes the session and navigate to login screen
  logout(): void {
    this.cleanSession();
    this.dialogRef.closeAll();
    this.router.navigate([this.getTenantId()]).catch((reason: any) => console.log(reason));
  }

  // removes all the session storage
  cleanSession(): void {
    this.inMemoryStorage = '';
    sessionStorage.clear();
    this.sharedService.loaderSubject.next(false);
    this.isLoggedIn.next(false);
  }

  // get all the authentication information incldes tokens, activitytime, logged in info
  get inMemoryStorage(): string {
    return sessionStorage.getItem(this.getTenantId()) as string;
  }

  // get all the authentication information tokens, activitytime, logged in info
  set inMemoryStorage(data: string) {
    sessionStorage.setItem(this.getTenantId(), data);
  }

  // check for id token still authenticated or not
  isTokenExpired(): boolean {
    const isExpired = this.getJwtInfo('isExpired');
    return isExpired;
  }

  authenticated(): boolean {
    if (this.hasToken()) {
      return true;
    }
    return false;
  }

  // Get detail information of jwt token, need to pass key value in parameter
  getJwtInfo(value: string = '', idToken: string = this.getIdToken()): any {
    const helper = new JwtHelperService();
    const tokenInfo = helper.decodeToken(idToken);
    switch (value) {
      case 'acr':
        return tokenInfo?.acr;
      case 'emailId':
        return tokenInfo?.preferred_username;
      case 'isExpired':
        return helper.isTokenExpired(idToken);
      default:
        return tokenInfo;
    }
  }

  // encrypt the string with private password
  encryptKey(msg: any, pass: any): string {
    const keySize = 256;
    const iterations = 1000;

    // Creates a word array filled with random bytes.
    // @param {number} in Bytes The number of random bytes to generate.
    const salt = CryptoJS.lib.WordArray.random(16);

    // //PBKDF2 is a password-based key derivation function.
    const key = CryptoJS.PBKDF2(pass, salt, {
      keySize: keySize / 32,
      iterations: iterations
    });

    const iv = CryptoJS.lib.WordArray.random(16);

    const encrypted = CryptoJS.AES.encrypt(msg, key, {
      iv: iv,
      padding: CryptoJS.pad.Pkcs7,
      mode: CryptoJS.mode.CBC
    });

    // salt, iv will be hex 32 in length
    // append them to the ciphertext for use  in decryption
    const transitmessage = salt.toString() + iv.toString() + encrypted.toString();

    return transitmessage;
  }

  // decrypt the string with private password
  decryptKey(transitmessage: string, pass: string): string {
    const keySize = 256;
    const iterations = 1000;
    const salt = CryptoJS.enc.Hex.parse(transitmessage.substr(0, 32));
    const iv = CryptoJS.enc.Hex.parse(transitmessage.substr(32, 32));
    const encrypted = transitmessage.substring(64);

    const key = CryptoJS.PBKDF2(pass, salt, {
      keySize: keySize / 32,
      iterations: iterations
    });

    const decrypted = CryptoJS.AES.decrypt(encrypted, key, {
      iv: iv,
      padding: CryptoJS.pad.Pkcs7,
      mode: CryptoJS.mode.CBC
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  // get decrypted info
  getDecodedInfo(item: string): any {
    if (this.inMemoryStorage) {
      const tenantInfo = this.inMemoryStorage;
      const userInfo = JSON.parse(this.decodeDataFromStorage(tenantInfo));
      switch (item) {
        case 'accessToken':
          return userInfo.accessToken;
        case 'idToken':
          return userInfo.idToken;
        case 'authType':
          return userInfo.authType;
        case 'expiresIn':
          return userInfo.expiresIn;
        case 'inactivityTimeDuration':
          return userInfo.inactivityTimeDuration;
        case 'inactivityTimeValidDuration':
          return userInfo.inactivityTimeValidDuration;
        case 'displayName':
          return userInfo.displayName;
        default:
          return userInfo;
      }
    }
  }

  // get tenant id from the url path
  getTenantId(): string {
    const pathName = window.location.pathname;
    const tenantId = pathName.split('/')[1];
    return tenantId;
  }

  addToStorage(valueToAdd: any): void {
    let userInfo = valueToAdd;

    if (this.inMemoryStorage) {
      const tenantInfo = this.inMemoryStorage;
      const existingUserInfo = JSON.parse(this.decodeDataFromStorage(tenantInfo));

      userInfo = {
        ...existingUserInfo,
        ...valueToAdd
      };
    }

    this.encodeStorage(userInfo);
  }

  removeFromSessionStorage(propertiesToRemove: string[]): void {
    if (this.inMemoryStorage) {
      const tenantInfo = this.inMemoryStorage;
      const existingUserInfo = JSON.parse(this.decodeDataFromStorage(tenantInfo));

      propertiesToRemove.forEach((property) => {
        if (Object.hasOwn(existingUserInfo as object, property)) {
          delete existingUserInfo[property];
        }
      });

      this.encodeStorage(existingUserInfo);
    }
  }

  encodeStorage(value: any): void {
    const encodedData = this.encodeDataForStorage(JSON.stringify(value));
    this.inMemoryStorage = encodedData;
  }

  encodeDataForStorage(data: string) {
    return btoa(data);
  }

  decodeDataFromStorage(encodedData: string) {
    return atob(encodedData);
  }

  validateTenant(): Observable<any> {
    const tenantId = this.getTenantId();
    const apiUrl = '/idp/' + tenantId + '/authn/jwks';
    return this.httpClient.get<any>(apiUrl);
  }

  revokeToken(): void {
    const revokePayload = `token=${encodeURIComponent(
      this.getDecodedInfo('accessToken') as string
    )}&client_id=ssp_clientid_${this.getTenantId()}&token_type_hint=access_token`;
    this.sharedService.revoke(this.getTenantId(), revokePayload).subscribe({
      next: () => {
        this.logout();
      },
      error: () => {
        this.logout();
      }
    });
  }
}
